import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function POCTest() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runPOC = async () => {
    setLoading(true);
    try {
      toast.loading("Running POC...");
      await axios.post(`${API}/dev/poc-run`);
      const res = await axios.get(`${API}/dev/poc-result`);
      setResult(res.data);
      toast.dismiss();
      toast.success("POC completed!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed: " + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>

        <div className="text-center">
          <h1 className="text-5xl font-bold mb-2">StockSense POC</h1>
          <p className="text-gray-400">Market Data → News → Sentiment → Prediction</p>
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Run POC Pipeline</h2>
          <Button onClick={runPOC} disabled={loading} size="lg">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {loading ? "Running..." : "Run POC Test"}
          </Button>
        </Card>

        {result && result.prediction ? (
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-4">{result.prediction.symbol.replace(".NS", "")}</h2>
            <div className="space-y-4">
              <div>
                <p className="text-gray-400 text-sm">Direction</p>
                <p className="text-2xl font-semibold">{result.prediction.direction}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Sentiment</p>
                <Badge>{result.prediction.sentiment_rating}</Badge>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Expected Return (1 Day)</p>
                <p className="text-3xl font-bold">
                  {result.prediction.expected_return_range.min}% to {result.prediction.expected_return_range.max}%
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Confidence</p>
                  <p className="text-xl font-semibold">{result.prediction.confidence}%</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Risk</p>
                  <Badge variant="outline">{result.prediction.risk_level}</Badge>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Score</p>
                  <p className="text-xl font-semibold">{result.prediction.sentiment_score}/100</p>
                </div>
              </div>
            </div>
          </Card>
        ) : null}

        <p className="text-xs text-gray-500 text-center">
          For informational purposes only. This is a POC demonstration.
        </p>
      </div>
    </div>
  );
}
